import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronDown, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useInventory } from "../hooks/useInventory";

const fmtPrice = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " €";

const statusBadge = {
  Available: "bg-emerald-900/30 text-emerald-300 border-emerald-500/40",
  Reserved: "bg-gold/15 text-gold border-gold/40",
  Sold: "bg-red-900/30 text-red-300 border-red-500/40",
};

export const ApartmentTable = ({ apartments, route }) => {
  const { t } = useLang();
  const inventory = useInventory();
  const [sortKey, setSortKey] = useState("number");
  const [sortDir, setSortDir] = useState("asc");
  const [fStatus, setFStatus] = useState("");
  const [fRooms, setFRooms] = useState("");
  const [expanded, setExpanded] = useState(null);

  const statusOf = (a) => inventory[a.code] || a.defaultStatus || "Available";
  const terraceLabel = (a) => (a.terraceType === "terrace" ? t("common.terraceLabel") : t("common.loggiaLabel"));
  const statusLabel = (s) => t(`common.${s.toLowerCase()}`);
  const fmtOrientation = (a) =>
    a.orientationKeys && a.orientationKeys.length
      ? a.orientationKeys.map((d) => t(`spec.dir_${d}`)).join(" / ")
      : "—";
  const fmtArea = (v) => (v || v === 0 ? `${v} m²` : "—");

  const extraFields = (a) => [
    { label: t("common.totalApartmentArea"), value: fmtArea(a.apartmentArea) },
    { label: t("common.rooms"), value: `${a.rooms} + ${terraceLabel(a)}` },
    { label: t("common.windowOrientation"), value: fmtOrientation(a) },
    { label: t("common.terraceArea"), value: fmtArea(a.terraceArea) },
    { label: t("common.landPlotArea"), value: fmtArea(a.landPlotArea) },
  ];

  const roomOptions = useMemo(
    () => [...new Set(apartments.map((a) => a.rooms))].sort((x, y) => x - y),
    [apartments]
  );

  const rows = useMemo(() => {
    let list = apartments.map((a) => ({ ...a, status: statusOf(a) }));
    if (fStatus) list = list.filter((a) => a.status === fStatus);
    if (fRooms) list = list.filter((a) => String(a.rooms) === fRooms);
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartments, inventory, fStatus, fRooms, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const toggleExpand = (code) => setExpanded((prev) => (prev === code ? null : code));

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ChevronsUpDown size={13} className="opacity-40" />;
    return sortDir === "asc" ? <ArrowUp size={13} className="text-gold" /> : <ArrowDown size={13} className="text-gold" />;
  };

  const columns = [
    { key: "number", label: t("common.apartment") },
    { key: "rooms", label: t("common.rooms") },
    { key: "area", label: t("common.area") },
    { key: "floor", label: t("common.floor") },
    { key: "price", label: t("common.price") },
    { key: "status", label: t("building.filterStatus") },
  ];

  const selectCls =
    "bg-charcoal-light border border-cream/20 text-cream text-sm px-4 py-2.5 outline-none focus:border-gold appearance-none rounded";

  return (
    <div data-testid="apartment-table-wrap">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={selectCls}>
          <option value="" className="bg-charcoal">{t("building.filterStatus")}: {t("building.allStatuses")}</option>
          <option value="Available" className="bg-charcoal">{t("common.available")}</option>
          <option value="Reserved" className="bg-charcoal">{t("common.reserved")}</option>
          <option value="Sold" className="bg-charcoal">{t("common.sold")}</option>
        </select>
        <select data-testid="filter-rooms" value={fRooms} onChange={(e) => setFRooms(e.target.value)} className={selectCls}>
          <option value="" className="bg-charcoal">{t("building.filterRooms")}: {t("building.allRooms")}</option>
          {roomOptions.map((r) => (
            <option key={r} value={String(r)} className="bg-charcoal">{r}</option>
          ))}
        </select>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto border border-cream/10 rounded-lg">
        <table className="w-full text-sm" data-testid="apartment-table">
          <thead>
            <tr className="border-b border-cream/10 text-left text-cream/50 uppercase tracking-widest text-[11px]">
              <th className="px-4 py-4 w-8"></th>
              {columns.map((c) => (
                <th key={c.key} className="px-4 py-4">
                  <button data-testid={`sort-${c.key}`} onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1.5 hover:text-cream transition-colors uppercase tracking-widest">
                    {c.label} <SortIcon col={c.key} />
                  </button>
                </th>
              ))}
              <th className="px-4 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <React.Fragment key={a.code}>
                <tr className="border-b border-cream/5 hover:bg-cream/5 transition-colors" data-testid={`apt-row-${a.unit}`}>
                  <td className="px-4 py-4">
                    <button
                      data-testid={`apt-expand-${a.unit}`}
                      onClick={() => toggleExpand(a.code)}
                      aria-label={expanded === a.code ? t("common.showLess") : t("common.showMore")}
                      className="text-cream/50 hover:text-gold transition-colors"
                    >
                      <ChevronDown size={16} className={`transition-transform duration-200 ${expanded === a.code ? "rotate-180 text-gold" : ""}`} />
                    </button>
                  </td>
                  <td className="px-4 py-4 text-cream font-medium">{a.unit}</td>
                  <td className="px-4 py-4 text-cream/70">{a.rooms} <span className="text-cream/40 text-xs">+ {terraceLabel(a)}</span></td>
                  <td className="px-4 py-4 text-cream/70">{a.area} m²</td>
                  <td className="px-4 py-4 text-cream/70">{a.floor}/{a.floors}</td>
                  <td className="px-4 py-4 text-gold whitespace-nowrap">{a.price != null ? fmtPrice(a.price) : "—"}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${statusBadge[a.status]}`}>{statusLabel(a.status)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <Link to={`/${route}/${a.unit}`} data-testid={`apt-view-${a.unit}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-gold hover:gap-2 transition-all">
                      {t("building.viewDetails")} <ArrowRight size={13} />
                    </Link>
                  </td>
                </tr>
                <AnimatePresence initial={false}>
                  {expanded === a.code && (
                    <tr data-testid={`apt-expand-row-${a.unit}`}>
                      <td colSpan={8} className="p-0 border-b border-cream/5 bg-charcoal/40">
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-cream/10 m-4 rounded-lg overflow-hidden border border-cream/10">
                            {extraFields(a).map((f, i) => (
                              <div key={i} className="bg-charcoal p-4">
                                <p className="text-[10px] uppercase tracking-widest text-cream/40">{f.label}</p>
                                <p className="mt-1 text-cream">{f.value}</p>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </React.Fragment>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-cream/40">—</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {rows.map((a) => (
          <div key={a.code} className="border border-cream/10 rounded-lg p-4" data-testid={`apt-card-${a.unit}`}>
            <div className="flex items-center justify-between">
              <span className="text-cream font-medium text-lg">{a.unit}</span>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${statusBadge[a.status]}`}>{statusLabel(a.status)}</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-sm text-cream/70">
              <span>{t("common.rooms")}: {a.rooms} + {terraceLabel(a)}</span>
              <span>{t("common.area")}: {a.area} m²</span>
              <span>{t("common.floor")}: {a.floor}/{a.floors}</span>
              {a.price != null && <span className="text-gold">{fmtPrice(a.price)}</span>}
            </div>

            <button
              data-testid={`apt-expand-m-${a.unit}`}
              onClick={() => toggleExpand(a.code)}
              className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-cream/60 hover:text-gold transition-colors"
            >
              {expanded === a.code ? t("common.showLess") : t("common.showMore")}
              <ChevronDown size={14} className={`transition-transform duration-200 ${expanded === a.code ? "rotate-180 text-gold" : ""}`} />
            </button>

            <AnimatePresence initial={false}>
              {expanded === a.code && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                  data-testid={`apt-expand-row-m-${a.unit}`}
                >
                  <div className="grid grid-cols-1 gap-px bg-cream/10 mt-3 rounded-lg overflow-hidden border border-cream/10">
                    {extraFields(a).map((f, i) => (
                      <div key={i} className="bg-charcoal p-3 flex items-center justify-between">
                        <span className="text-[11px] uppercase tracking-widest text-cream/40">{f.label}</span>
                        <span className="text-cream text-sm">{f.value}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <Link to={`/${route}/${a.unit}`} data-testid={`apt-view-m-${a.unit}`} className="mt-3 w-full flex items-center justify-center gap-2 border border-gold/40 text-gold text-xs uppercase tracking-widest py-2.5 hover:bg-gold/10 transition-colors">
              {t("building.viewDetails")} <ArrowRight size={14} />
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};
